import { CSSProperties, useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { ChatMessage } from 'lib/twitchChatParse'
import { sampleNextChangeAt, sampleWindowAt, staticSampleWindow } from 'lib/overlaySamples'

export const FEED_MAX_ROWS = 100
const SEEN_TRIM_AT = 4000
const SEEN_KEEP = 2000
export const FEED_RELEASE_GRACE_MS = 30000

export interface YouTubeChannelMeta {
  title: string | null
  imageUrl: string | null
}

export interface ChatFeedSnapshot {
  readonly real: readonly ChatMessage[]
  readonly realSeen: boolean
  readonly youtubeSeen: boolean
  readonly ytChannels: Readonly<Record<string, YouTubeChannelMeta>>
}

export interface PreviewFeedStats {
  keys: number
  rows: number
  seen: number
  graceTimers: number
  listeners: number
}

export interface ChatPreviewFeed {
  hold: (key: string) => () => void
  appendReal: (key: string, rows: readonly ChatMessage[]) => void
  removeReal: (key: string, hit: (m: ChatMessage) => boolean) => void
  noteYouTubeChannel: (key: string, channelId: string, meta: YouTubeChannelMeta) => void
  subscribe: (key: string, cb: () => void) => () => void
  snapshot: (key: string) => ChatFeedSnapshot
  stats: () => PreviewFeedStats
  dispose: () => void
}

export interface WallPreviewFeed {
  hold: (key: string) => () => void
  markReal: (key: string) => void
  isReal: (key: string) => boolean
  subscribe: (key: string, cb: () => void) => () => void
  stats: () => PreviewFeedStats
  dispose: () => void
}

type Timer = ReturnType<typeof globalThis.setTimeout>

const noop = (): void => {}

const EMPTY_CHAT_SNAPSHOT: ChatFeedSnapshot = Object.freeze({
  real: Object.freeze([]),
  realSeen: false,
  youtubeSeen: false,
  ytChannels: Object.freeze({})
})

interface HeldEntry {
  holders: number
  graceTimer: Timer | undefined
}

interface Registry<E extends HeldEntry> {
  entries: Map<string, E>
  entryFor: (key: string) => E | null
  hold: (key: string) => () => void
  armGrace: (key: string, e: E) => void
  notify: (key: string) => void
  subscribe: (key: string, cb: () => void) => () => void
  listenerCount: () => number
  graceCount: () => number
  dispose: () => void
}

const createRegistry = <E extends HeldEntry>(fresh: () => E): Registry<E> => {
  const entries = new Map<string, E>()
  const listeners = new Map<string, Set<() => void>>()
  let disposed = false

  const notify = (key: string): void => {
    const set = listeners.get(key)
    if (set == null) return
    Array.from(set).forEach((cb) => cb())
  }

  const entryFor = (key: string): E | null => {
    if (disposed) return null
    const cur = entries.get(key)
    if (cur != null) return cur
    const created = fresh()
    entries.set(key, created)
    return created
  }

  const armGrace = (key: string, e: E): void => {
    if (disposed || e.holders > 0 || e.graceTimer != null) return
    e.graceTimer = globalThis.setTimeout(() => {
      e.graceTimer = undefined
      if (entries.get(key) !== e || e.holders > 0) return
      entries.delete(key)
      notify(key)
    }, FEED_RELEASE_GRACE_MS)
  }

  const hold = (key: string): (() => void) => {
    const e = entryFor(key)
    if (e == null) return noop
    e.holders += 1
    if (e.graceTimer != null) {
      globalThis.clearTimeout(e.graceTimer)
      e.graceTimer = undefined
    }
    let released = false
    return () => {
      if (released) return
      released = true
      if (entries.get(key) !== e) return
      e.holders = Math.max(0, e.holders - 1)
      armGrace(key, e)
    }
  }

  const subscribe = (key: string, cb: () => void): (() => void) => {
    if (disposed) return noop
    const set = listeners.get(key) ?? new Set<() => void>()
    set.add(cb)
    listeners.set(key, set)
    return () => {
      const cur = listeners.get(key)
      if (cur == null) return
      cur.delete(cb)
      if (cur.size === 0) listeners.delete(key)
    }
  }

  const listenerCount = (): number =>
    Array.from(listeners.values()).reduce((sum, set) => sum + set.size, 0)

  const graceCount = (): number =>
    Array.from(entries.values()).filter((e) => e.graceTimer != null).length

  const dispose = (): void => {
    disposed = true
    entries.forEach((e) => {
      if (e.graceTimer != null) globalThis.clearTimeout(e.graceTimer)
      e.graceTimer = undefined
    })
    entries.clear()
    listeners.clear()
  }

  return { entries, entryFor, hold, armGrace, notify, subscribe, listenerCount, graceCount, dispose }
}

interface ChatEntry extends HeldEntry {
  seen: Set<string>
  snapshot: ChatFeedSnapshot
}

export const createChatPreviewFeed = (): ChatPreviewFeed => {
  const reg = createRegistry<ChatEntry>(() => ({
    holders: 0,
    graceTimer: undefined,
    seen: new Set<string>(),
    snapshot: EMPTY_CHAT_SNAPSHOT
  }))

  const publish = (key: string, e: ChatEntry, next: ChatFeedSnapshot): void => {
    e.snapshot = next
    reg.notify(key)
    reg.armGrace(key, e)
  }

  const rememberSeen = (e: ChatEntry, id: string): void => {
    e.seen.add(id)
    if (e.seen.size > SEEN_TRIM_AT) {
      const keep = Array.from(e.seen).slice(-SEEN_KEEP)
      e.seen.clear()
      keep.forEach((k) => e.seen.add(k))
    }
  }

  return {
    hold: reg.hold,
    appendReal: (key, rows) => {
      if (rows.length === 0) return
      const e = reg.entryFor(key)
      if (e == null) return
      const fresh = rows.filter((m) => {
        if (e.seen.has(m.id)) return false
        rememberSeen(e, m.id)
        return true
      })
      if (fresh.length === 0) return
      const cur = e.snapshot
      publish(key, e, Object.freeze({
        real: Object.freeze([...cur.real, ...fresh].slice(-FEED_MAX_ROWS)),
        realSeen: true,
        youtubeSeen: cur.youtubeSeen || fresh.some((m) => m.platform === 'YOUTUBE'),
        ytChannels: cur.ytChannels
      }))
    },
    removeReal: (key, hit) => {
      const e = reg.entries.get(key)
      if (e == null) return
      const cur = e.snapshot
      const kept = cur.real.filter((m) => !hit(m))
      if (kept.length === cur.real.length) return
      publish(key, e, Object.freeze({ ...cur, real: Object.freeze(kept) }))
    },
    noteYouTubeChannel: (key, channelId, meta) => {
      if (channelId === '') return
      const e = reg.entryFor(key)
      if (e == null) return
      const cur = e.snapshot
      const prev = cur.ytChannels[channelId]
      const title = meta.title ?? prev?.title ?? null
      const imageUrl = meta.imageUrl ?? prev?.imageUrl ?? null
      if (prev != null && prev.title === title && prev.imageUrl === imageUrl) return
      publish(key, e, Object.freeze({
        ...cur,
        ytChannels: Object.freeze({ ...cur.ytChannels, [channelId]: { title, imageUrl } })
      }))
    },
    subscribe: reg.subscribe,
    snapshot: (key) => reg.entries.get(key)?.snapshot ?? EMPTY_CHAT_SNAPSHOT,
    stats: () => ({
      keys: reg.entries.size,
      rows: Array.from(reg.entries.values()).reduce((sum, e) => sum + e.snapshot.real.length, 0),
      seen: Array.from(reg.entries.values()).reduce((sum, e) => sum + e.seen.size, 0),
      graceTimers: reg.graceCount(),
      listeners: reg.listenerCount()
    }),
    dispose: reg.dispose
  }
}

interface WallEntry extends HeldEntry {
  real: boolean
}

export const createWallPreviewFeed = (): WallPreviewFeed => {
  const reg = createRegistry<WallEntry>(() => ({ holders: 0, graceTimer: undefined, real: false }))
  return {
    hold: reg.hold,
    markReal: (key) => {
      const e = reg.entryFor(key)
      if (e == null || e.real) return
      e.real = true
      reg.notify(key)
      reg.armGrace(key, e)
    },
    isReal: (key) => reg.entries.get(key)?.real === true,
    subscribe: reg.subscribe,
    stats: () => ({
      keys: reg.entries.size,
      rows: 0,
      seen: 0,
      graceTimers: reg.graceCount(),
      listeners: reg.listenerCount()
    }),
    dispose: reg.dispose
  }
}

const noopSubscribe = (): (() => void) => noop
const falseSnapshot = (): boolean => false
const trueSnapshot = (): boolean => true

export const useChatFeedSnapshot = (feed: ChatPreviewFeed | null, key: string): ChatFeedSnapshot | null => {
  const subscribe = useCallback(
    (cb: () => void): (() => void) => (feed == null ? noop : feed.subscribe(key, cb)),
    [feed, key]
  )
  const getSnapshot = useCallback(
    (): ChatFeedSnapshot | null => (feed == null ? null : feed.snapshot(key)),
    [feed, key]
  )
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export const useWallFeedReal = (feed: WallPreviewFeed | null, key: string): boolean => {
  const subscribe = useCallback(
    (cb: () => void): (() => void) => (feed == null ? noop : feed.subscribe(key, cb)),
    [feed, key]
  )
  const getSnapshot = useCallback(
    (): boolean => (feed == null ? false : feed.isReal(key)),
    [feed, key]
  )
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
let reducedMotionResolved = false
let reducedMotionList: MediaQueryList | null = null

const reducedMotionMql = (): MediaQueryList | null => {
  if (!reducedMotionResolved) {
    reducedMotionResolved = true
    reducedMotionList = typeof globalThis.matchMedia === 'function'
      ? globalThis.matchMedia(REDUCED_MOTION_QUERY)
      : null
  }
  return reducedMotionList
}

const subscribeReducedMotion = (cb: () => void): (() => void) => {
  const mql = reducedMotionMql()
  if (mql == null) return noop
  mql.addEventListener('change', cb)
  return () => mql.removeEventListener('change', cb)
}

const reducedMotionSnapshot = (): boolean => reducedMotionMql()?.matches === true

export const usePrefersReducedMotion = (enabled: boolean): boolean =>
  useSyncExternalStore(
    enabled ? subscribeReducedMotion : noopSubscribe,
    enabled ? reducedMotionSnapshot : falseSnapshot,
    falseSnapshot
  )

const subscribeVisibility = (cb: () => void): (() => void) => {
  if (typeof globalThis.document === 'undefined') return noop
  globalThis.document.addEventListener('visibilitychange', cb)
  return () => globalThis.document.removeEventListener('visibilitychange', cb)
}

const visibilitySnapshot = (): boolean =>
  typeof globalThis.document === 'undefined' || globalThis.document.visibilityState !== 'hidden'

export const useDocumentVisible = (enabled: boolean): boolean =>
  useSyncExternalStore(
    enabled ? subscribeVisibility : noopSubscribe,
    enabled ? visibilitySnapshot : trueSnapshot,
    trueSnapshot
  )

const EMPTY_WINDOW: readonly number[] = Object.freeze([])

const sameWindow = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((n, i) => n === b[i])

export const useSampleChatWindow = ({
  enabled,
  seed,
  reducedMotion,
  visible
}: {
  enabled: boolean
  seed: number
  reducedMotion: boolean
  visible: boolean
}): readonly number[] => {
  const [win, setWin] = useState<readonly number[]>(() => {
    if (!enabled) return EMPTY_WINDOW
    return reducedMotion ? staticSampleWindow() : sampleWindowAt(Date.now(), seed)
  })
  useEffect(() => {
    if (!enabled) return
    if (reducedMotion) {
      const still = staticSampleWindow()
      setWin((prev) => (sameWindow(prev, still) ? prev : still))
      return
    }
    if (!visible) return
    let timer: Timer | undefined
    const step = (): void => {
      const now = Date.now()
      const next = sampleWindowAt(now, seed)
      setWin((prev) => (sameWindow(prev, next) ? prev : next))
      timer = globalThis.setTimeout(step, Math.max(1, sampleNextChangeAt(now, seed) - now))
    }
    step()
    return () => globalThis.clearTimeout(timer)
  }, [enabled, seed, reducedMotion, visible])
  return enabled ? win : EMPTY_WINDOW
}

const SAMPLE_TAG_FONT = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif'

export const SAMPLE_TAG_MAX_PX = 24
export const sampleTagFontPx = (fontSizePx: number): number =>
  Math.min(SAMPLE_TAG_MAX_PX, Math.max(12, Number.isFinite(fontSizePx) ? Math.round(fontSizePx) : 0))

export const wallSampleTagBasePx = (emoteSize: number): number =>
  Math.round((Number.isFinite(emoteSize) ? emoteSize : 0) * 0.4)

export const SAMPLE_TAG_SCALE_VAR = '--fd-sample-tag-scale'

export const sampleTagStyle = (fontSizePx: number): CSSProperties => ({
  position: 'absolute',
  top: `calc(8px * var(${SAMPLE_TAG_SCALE_VAR}, 1))`,
  right: `calc(8px * var(${SAMPLE_TAG_SCALE_VAR}, 1))`,
  transform: `scale(var(${SAMPLE_TAG_SCALE_VAR}, 1))`,
  transformOrigin: 'top right',
  zIndex: 1,
  pointerEvents: 'none',
  padding: '2px 8px',
  borderRadius: 4,
  background: 'rgba(0, 0, 0, 0.6)',
  color: 'rgba(255, 255, 255, 0.9)',
  fontFamily: SAMPLE_TAG_FONT,
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: `${sampleTagFontPx(fontSizePx)}px`,
  lineHeight: 1.25,
  letterSpacing: '0.02em',
  textShadow: 'none',
  whiteSpace: 'nowrap'
})
